import type { ScoreRecord, SessionRecord, TrialRecord } from '../types';
import type { LearningSession } from '../learning';

/** RFC 4180. 셀에 쉼표·따옴표·개행이 있으면 감싼다. 숫자는 그대로. */
function cell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  // Protect text cells opened in spreadsheet software; numerical values stay numerical.
  const raw = String(v);
  const s = /^[=+@\-\t\r]/.test(raw) ? "'" + raw : raw;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
const row = (xs: unknown[]): string => xs.map(cell).join(',');

export function learningCsv(records: readonly LearningSession[]): string {
  const lines = [row(['participant', 'session_id', 'mode', 'episode', 'content_version', 'content_status', 'protocol_version', 'app_version', 'seed', 'started_at', 'status', 'item_id', 'choice_order', 'selected', 'target_matched', 'response_ms', 'reading_ms', 'fragment_attempts', 'answered_at'])];
  for (const r of records) for (const response of r.responses) {
    lines.push(row([r.participantCode, r.id, r.mode, r.episodeId, r.contentVersion, r.contentStatus, r.protocolVersion, r.appVersion, r.seed, r.startedAt, r.status, response.itemId, JSON.stringify(response.choiceOrder), response.selected, response.correct, response.responseMs, response.readingMs, JSON.stringify(response.fragmentAttempts), response.answeredAt]));
  }
  return lines.join('\r\n') + '\r\n';
}

/**
 * trials_long.csv — 한 행 = 한 트라이얼. R/JASP 에서 바로 읽히는 롱 포맷.
 * sign_convention 을 매 행에 실어 분석 단계의 부호 혼동을 원천 차단한다.
 */
export function trialsLongCsv(sessions: readonly SessionRecord[], trials: readonly TrialRecord[]): string {
  const byId = new Map(sessions.map((s) => [s.id, s]));
  const header = [
    'participant', 'session_id', 'session_started_at', 'arm', 'supervision', 'protocol', 'protocol_version', 'rng_seed',
    'block', 'trial_index', 'trial_type', 'pair_id', 'threat_side', 'probe_side', 'congruency', 'soa_ms',
    'rt_ms', 'correct', 'timed_out', 'aborted', 'response', 'input_source', 'handler_delay_ms', 'premature_inputs',
    'cue_requested_ms', 'cue_actual_ms', 'cue_dropped_frames', 'refresh_hz', 'physical_layout', 'sign_convention',
  ];
  const lines = [row(header)];
  for (const t of trials) {
    const s = byId.get(t.sessionId);
    const cue = t.phases.find((p) => p.id === 'cue');
    lines.push(row([
      s?.participantCode ?? '', t.sessionId, s?.startedAt ?? '', t.arm, s?.supervision ?? '', s?.protocolId ?? '', s?.protocolVersion ?? '', s?.rngSeed ?? '',
      t.blockId, t.trialIndex, t.trialType, t.pairId, t.threatSide, t.probeSide, t.congruency, t.soaMs,
      t.rt, t.correct, t.timedOut, t.aborted, t.response, t.inputSource, t.handlerDelayMs, t.prematureInputs,
      cue?.requestedMs ?? '', cue?.actualMs ?? '', cue?.droppedFrames ?? '', s?.device.refreshHz ?? '', s?.device.physicalLayout ?? '', s?.signConvention ?? 'vigilance-positive',
    ]));
  }
  return lines.join('\r\n') + '\r\n';
}

/** scores.csv — 한 행 = 한 세션 채점. 파생 지표. */
export function scoresCsv(sessions: readonly SessionRecord[], scores: readonly ScoreRecord[]): string {
  const byId = new Map(sessions.map((s) => [s.id, s]));
  const header = [
    'participant', 'session_id', 'session_started_at', 'arm', 'supervision', 'scoring_version', 'computed_at',
    'valid_trials', 'total_trials', 'removed_fraction', 'participant_excluded', 'accuracy', 'attempted', 'answered', 'correct_count', 'timeout_count', 'response_accuracy',
    'bs_four_cell_harmonic', 'bs_simple_mean', 'bs_simple_se', 'bs_lower_probe_only', 'bs_half_odd', 'bs_half_even',
    'rt_congruent_mean', 'rt_congruent_sd', 'rt_congruent_n', 'rt_incongruent_mean', 'rt_incongruent_sd', 'rt_incongruent_n',
    'tlbs_mean_positive', 'tlbs_mean_negative', 'tlbs_variability', 'tlbs_n', 'participant_rt_sd', 'participant_rt_mean', 'abv', 'abv_bins', 'abv_expected_bins', 'tlbs_variability_method',
    'refresh_hz', 'frame_sd_ms', 'dropped_pct', 'physical_layout', 'sign_convention',
  ];
  const lines = [row(header)];
  for (const r of scores) {
    const s = byId.get(r.sessionId);
    const b = r.scores.bias, t = r.scores.tlbs, p = r.scores.preprocess;
    lines.push(row([
      s?.participantCode ?? '', r.sessionId, s?.startedAt ?? '', r.arm, s?.supervision ?? '', r.scoringVersion, r.computedAt,
      r.scores.validTrials, p.total, p.removedFraction, p.participantExcluded, r.scores.accuracy, r.scores.performance?.attempted, r.scores.performance?.answered, r.scores.performance?.correct, r.scores.performance?.timedOut, r.scores.performance?.responseAccuracy,
      b.fourCellHarmonic, b.simpleMean, b.simpleMeanSe, b.lowerProbeOnly, b.halves.odd, b.halves.even,
      b.congruent?.mean ?? null, b.congruent?.sd ?? null, b.congruent?.n ?? null, b.incongruent?.mean ?? null, b.incongruent?.sd ?? null, b.incongruent?.n ?? null,
      t.meanPositive, t.meanNegative, t.variability, t.n, t.participantRtSd, t.participantRtMean, r.scores.abv.abv, r.scores.abv.bins, r.scores.abv.expectedBins, 'successive-MAD',
      s?.device.refreshHz ?? '', s?.device.frameSdMs ?? '', s?.quality?.droppedPct ?? '', s?.device.physicalLayout ?? '', b.signConvention,
    ]));
  }
  return lines.join('\r\n') + '\r\n';
}
